import { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';

const companyFields = [
 ['company_name','Company Name',120], ['trading_name','Trading Name',120], ['abn','ABN',40],
 ['address','Address',400], ['address_line_2','Address Line 2',160], ['suburb','Suburb',100],
 ['state','State',80], ['postcode','Postcode',20], ['country','Country',80],
 ['phone','Phone',80], ['email','Email',160], ['website','Website',160],
];
const documentFields = [
 ['introduction','Introduction',600], ['notes','Default Notes',1200],
 ['supplier_note','Supplier Note',1200], ['footer','Quote Footer',300], ['terms','Quote Terms',2400],
];
export default function DocumentSettingsPage() {
 const { session }=useAuth(); const admin=session?.user?.role==='admin';
 const [form,setForm]=useState(null); const [state,setState]=useState('Loading…');
 const saved=useRef(null);
 const current=useRef(null), version=useRef(0), queue=useRef(Promise.resolve()), failed=useRef(false);
 useEffect(()=>{ api.get('/document-settings').then(({data})=>{current.current=data;saved.current=JSON.stringify({...data,version:0});version.current=data.version;setForm(data);setState('Saved');}).catch(()=>setState('Could not load settings. Reload to retry.')); },[]);
 const edit=(key,value)=>{const next={...current.current,[key]:value};current.current=next;setForm(next);setState('Unsaved changes');};
 const save=(snapshot=current.current)=>{
   if(!admin||!snapshot)return;
   setState('Saving…');
   queue.current=queue.current.then(async()=>{
     if(failed.current)return;
     const key=JSON.stringify({...snapshot,version:0});
     if(key===saved.current){setState(current.current===snapshot?"Saved":"Unsaved changes");return;}
     try{const {data}=await api.put('/document-settings',{...snapshot,version:version.current});version.current=data.version;saved.current=key;window.dispatchEvent(new Event('sq-branding-saved'));setState(current.current===snapshot?'Saved':'Unsaved changes');}
     catch(e){failed.current=true;setState(e.response?.status===409?'Changed elsewhere. Reload to review before saving.':'Save failed. Your edits are still here. Retry when connected.');}
   });
 };
 const upload=async(e,key)=>{
   const file=e.target.files?.[0];if(!file)return;
   if(!['image/png','image/jpeg'].includes(file.type)||file.size>350000){setState('Choose a PNG or JPEG smaller than 350 KB.');return;}
   const reader=new FileReader();reader.onload=()=>{edit(key,reader.result);save(current.current);};reader.readAsDataURL(file);
 };
 if(!form)return <div className="p-8">{state}</div>;
 const inputClass = "w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[12px] outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100";
 const logoCard = (key,title,description,dark) => <section className="sq-logo-section">
   <h2 className="text-sm font-semibold">{title}</h2>
   <p className="text-xs text-slate-500 mt-1 mb-4">{description}</p>
   <div className={`sq-logo-preview rounded-2xl border border-dashed grid place-items-center p-4 ${dark?'bg-[#07090d] border-slate-700':'bg-white border-slate-300'}`}>
     {form[key] ? <img alt={title} src={form[key]} className="max-h-[160px] max-w-full object-contain"/> : <span className="text-sm text-slate-400">No logo selected</span>}
   </div>
   <label className="sq-logo-upload">Select {title}
     <input aria-label={`Upload ${title.toLowerCase()}`} type="file" accept="image/png,image/jpeg" onChange={e=>upload(e,key)} className="sr-only"/>
   </label>
   {form[key] && <button onClick={()=>{edit(key,'');save(current.current);}} className="mt-3 text-xs text-rose-700">Remove {title.toLowerCase()}</button>}
 </section>;
 return <div className="sq-branding-page">
   <div className="flex justify-end gap-4 items-center mb-2">
     <h1 className="sr-only">Company &amp; Branding</h1>
     <div className="flex items-center gap-3"><span role="status" className="text-xs text-slate-500">{state}</span>
       {failed.current && <button className="px-3 py-2 rounded-lg bg-slate-900 text-white text-xs" onClick={()=>{failed.current=false;save();}}>Retry saving</button>}
     </div>
   </div>
   <fieldset disabled={!admin} className="sq-branding-grid">
     <div className="sq-branding-logos">
       {logoCard('app_logo_data_uri','App Logo','Used on the application sidebar.',true)}
       {logoCard('logo_data_uri','Document Logo','Used on quotes and PDFs.',false)}
       <section className="sq-logo-section grid grid-cols-2 gap-4">
         {[['accent_colour','Primary Colour'],['heading_colour','Document Heading']].map(([key,label])=><label key={key} className="text-xs font-medium">{label}<input aria-label={label} type="color" value={form[key]} onChange={e=>edit(key,e.target.value)} onBlur={()=>save()} className="block border rounded-lg mt-2 w-full h-10 p-1"/></label>)}
       </section>
     </div>
     <div className="space-y-5 min-w-0">
       <section className="bg-white border border-slate-200 rounded-[22px] p-5">
         <h2 className="text-sm font-semibold mb-4">Company Details</h2>
         <div className="sq-company-fields">
           {companyFields.map(([key,label,max])=><label key={key} className="min-w-0"><span className="block mb-1 text-[10px] uppercase tracking-[0.16em] text-slate-500">{label}</span><input value={form[key]||''} maxLength={max} onChange={e=>edit(key,e.target.value)} onBlur={()=>save()} className={inputClass}/></label>)}
         </div>
       </section>
       <section className="bg-white border border-slate-200 rounded-[22px] p-5">
         <h2 className="text-sm font-semibold mb-1">Quote Content</h2>
         
         <div className="grid gap-4 md:grid-cols-2">{documentFields.map(([key,label,max])=><label key={key} className={`text-xs font-medium ${key==='terms'?'md:col-span-2':''}`}><span className="block mb-2">{label}</span><textarea value={form[key]||''} rows={key==='terms'?4:3} maxLength={max} onChange={e=>edit(key,e.target.value)} onBlur={()=>save()} className={inputClass}/></label>)}</div>
       </section>
     </div>
   </fieldset>
 </div>;
}
